<template>
  <div class="px-5 py-4 pt-10" v-if="!loading && academy">
    <div class="pb-4">
      <div class="flex" style="gap: 0.5rem">
        <v-icon size="24" color="plusTextPrimary">mdi-cog-outline</v-icon>
        <div class="plusTextPrimary--text" style="font-size: 1.438rem">
          {{ $t('message.academy_settings_admin_plus.title') }}
        </div>
      </div>
      <div class="page-subtitle">
        {{ $t('message.academy_settings_admin_plus.title_explained') }}
      </div>
    </div>
    <v-alert v-if="showAcademyProfileBanner" dense elevation="0" color="#FFF8E1"
      class="d-flex align-center mb-4 py-4" :style="isDarkMode() ? { border: '1px solid #FFCC80', background: 'rgba(255, 204, 128, 0.1) !important' }:{border: '1px solid #FFCC80'}">
      <v-icon size="20" color="plusAmberDarken4">mdi-information-outline</v-icon>
      <span class="font-roboto text-body-1 text-left" :class="{'plusGreyDarken3--text': !isDarkMode(), 'plusTextOnSecondary--text': isDarkMode()}">
        {{ $t("message.academy_settings_admin_plus.complete_profile_banner") }}</span>
    </v-alert>
    <v-divider />
    <div class="pt-8">
      <v-tabs
        v-model="activeTab"
        slider-color="plusIndigoDarken1"
        color="plusTabActiveText"
      >
        <v-tab key="academyProfile">{{
          $t('message.academy_settings_admin_plus.profile_tab')
        }}</v-tab>
        <v-tab key="settings">{{
          $t('message.academy_settings_admin_plus.settings_tab')
        }}</v-tab>
      </v-tabs>
      <v-tabs-items class="pt-8" v-model="activeTab">
        <v-tab-item key="academyProfile">
          <div class="flex mt-6" style="gap:1.5rem;" :style="isSmallerDisplay() ? 'flex-direction:column-reverse' : ''">
            <div :style="isSmallerDisplay() ? 'width:100%' : 'max-width:23rem;width:100%'">
              <div class="flex flex-col pa-8 plusGreyLighten5 rounded"
                style="width:100%;gap:2rem;border:1px solid #EEE;">
                <div class="text-uppercase plusTextSecondary--text text-center" style="font-size:0.75rem">
                  {{ $t("message.academy_settings_admin_plus.public_preview") }}:</div>
                <div class="flex flex-col" style="gap:1rem;">
                  <div class="justify-center items-center d-flex">
                    <v-avatar size="150">
                      <ImageDisplay
                        :src="
                          academyImage ||
                          assets('logo-circular-watermarked.png')
                        "
                        style="max-width: 150px; max-height: 150px"
                      />
                    </v-avatar>
                  </div>
                  <div class="flex flex-col">
                    <div
                      class="plusTextPrimary--text text-center"
                      style="font-size: 1.438rem; word-break: break-word"
                    >
                      {{ academy.name }}
                    </div>
                    <div
                      class="plusTextPrimary--text text-center"
                      style="font-size: 0.875rem; word-break: break-word"
                    >
                      {{ academy.shortName }}
                    </div>
                    <div
                      class="plusTextSecondary--text pt-4"
                      style="font-size: 0.875rem; word-break: break-word"
                    >
                      {{ academy.description }}
                    </div>
                  </div>
                </div>
                <div class="flex flex-col" style="gap: 1rem">
                  <div class="flex" style="gap: 1rem">
                    <div
                      class="plusTextSecondary--text flex-shrink-0"
                      style="font-size: 0.875rem; line-height: 1.5rem"
                    >
                      {{
                        $t(
                          'message.academy_settings_admin_plus.public_username',
                        )
                      }}
                    </div>
                    <div style="word-break: break-word">
                      {{ academy.identifier }}
                    </div>
                  </div>
                  <div class="flex" style="gap: 1rem">
                    <div
                      class="plusTextSecondary--text flex-shrink-0"
                      style="font-size: 0.875rem; line-height: 1.5rem"
                    >
                      {{ $t('message.academy_settings_admin_plus.address') }}
                    </div>
                    <div style="word-break: break-word">
                      {{ academy.address }}
                    </div>
                  </div>
                  <div class="flex" style="gap: 1rem">
                    <div
                      class="plusTextSecondary--text flex-shrink-0"
                      style="font-size: 0.875rem; line-height: 1.5rem"
                    >
                      {{ $t('message.academy_settings_admin_plus.email') }}
                    </div>
                    <div style="word-break: break-word">
                      {{ academy.email }}
                    </div>
                  </div>
                  <div class="flex" style="gap: 1rem">
                    <div
                      class="plusTextSecondary--text flex-shrink-0"
                      style="font-size: 0.875rem; line-height: 1.5rem"
                    >
                      {{
                        $t('message.academy_settings_admin_plus.phone_number')
                      }}
                    </div>
                    <div style="word-break: break-word">
                      {{ academy.phone }}
                    </div>
                  </div>
                  <div v-if="publicPageUrl" class="flex" style="gap: 1rem">
                    <div
                      class="plusTextSecondary--text flex-shrink-0"
                      style="font-size: 0.875rem; line-height: 1.5rem"
                    >
                      {{
                        $t(
                          'message.academy_settings_admin_plus.public_page_url',
                        )
                      }}
                    </div>
                    <div style="word-break: break-word">
                      {{ publicPageUrl }}
                    </div>
                  </div>
                  <div
                    class="flex"
                    :class="{ 'd-none': !affiliation }"
                    style="gap: 1rem"
                  >
                    <div
                      class="plusTextSecondary--text"
                      style="font-size: 0.875rem; line-height: 1.5rem"
                    >
                      {{
                        $t(
                          'message.academy_settings_admin_plus.international_affiliation',
                        )
                      }}
                    </div>
                    <div>
                      <v-chip
                        color="plusTealLighten5"
                        class="plusTealDarken4--text text-weight-light"
                        >{{ affiliation }}</v-chip
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style="width: 100%">
              <v-expansion-panels v-model="profileExpansionPanel">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div
                      v-if="profileExpansionPanel !== 0"
                      class="flex align-center"
                      style="gap: 0.75rem"
                    >
                      <v-icon size="24" color="plusTextSecondary"
                        >mdi-bank-outline</v-icon
                      >
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 1rem"
                      >
                        {{
                          $t('message.academy_settings_admin_plus.academy_info')
                        }}
                      </div>
                    </div>
                    <div
                      v-if="profileExpansionPanel === 0"
                      class="flex align-center"
                      style="gap: 0.75rem"
                    >
                      <v-icon size="24" color="plusTextPrimary"
                        >mdi-bank-outline</v-icon
                      >
                      <div
                        class="plusTextPrimary--text"
                        style="font-size: 1.25rem"
                      >
                        {{
                          $t('message.academy_settings_admin_plus.academy_info')
                        }}
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="flex flex-col" style="gap:2rem;">
                      <div class="plusTextSecondary--text" style="font-size:0.875rem">
                        {{ $t("message.academy_settings_admin_plus.keep_academy_profile_updated") }}</div>
                      <div class="rounded flex flex-col" style="gap:0.5rem"
                        :class="{ 'missingAcademyLogoHighlight': showAcademyProfileBanner && !academy.image }">
                        <span :class="{ 'plusGreyLighten4--text': isDarkMode() , 'plusTextPrimary--text': !isDarkMode() }" style="font-size:1rem;">{{
                          $t("message.academy_settings_admin_plus.image_label")
                        }}*</span>
                        <div class="flex align-center plusGreyLighten5 pa-6" style="gap:1rem;">
                          <v-avatar size="64">
                            <ImageDisplay
                              :src="
                                academyImage ||
                                assets('logo-circular-watermarked.png')
                              "
                              style="max-width: 64px; max-height: 64px"
                            />
                          </v-avatar>
                          <PlusImageUploadCroppie
                            :label="
                              $t(
                                'message.academy_settings_admin_plus.change_logo',
                              )
                            "
                            @uploaded="onLogoUploaded"
                          >
                          </PlusImageUploadCroppie>
                        </div>
                      </div>
                      <div class="flex flex-col">
                        <v-text-field
                          dense
                          outlined
                          v-model="academy.name"
                          :label="$t('message.name')"
                          maxlength="80"
                          :class="{
                            missingAcademyProfileHighlight:
                              showAcademyProfileBanner && !academy.name,
                          }"
                        ></v-text-field>

                        <v-textarea
                          outlined
                          :label="$t('message.description')"
                          v-model="academy.description"
                          style="word-break: break-word"
                          :class="{
                            missingAcademyProfileHighlight:
                              showAcademyProfileBanner && !academy.description,
                          }"
                          :counter="350"
                          maxlength="350"
                          :rules="[
                            (v) => !v || v.length <= 350 || 'Max 350 characters',
                          ]"
                        ></v-textarea>
                        <Locations
                          :width="'100%'"
                          :label="$t('message.address')"
                          v-model="geolocatedAddress"
                          :display="academy.address"
                          :class="{ 'missingAcademyProfileHighlight': showAcademyProfileBanner && !academy.address }" />
                        <v-text-field dense outlined :label="$t('message.email')" v-model="academy.email"
                          maxlength="100" :rules="emailRules"
                          :class="{ 'missingAcademyProfileHighlight': showAcademyProfileBanner && !academy.email }"></v-text-field>
                        <v-text-field dense outlined v-model="academy.identifier"
                          :label="$t('message.public_page_id')"
                          maxlength="50"
                        ></v-text-field>
                        <v-text-field
                          dense
                          outlined
                          :label="$t('message.phone')"
                          v-model="academy.phone"
                          maxlength="25"
                        ></v-text-field>
                        <v-text-field
                          dense
                          outlined
                          :label="$t('message.web_site')"
                          v-model="academy.website"
                          maxlength="200"
                          :rules="websiteRules"
                        ></v-text-field>
                        <PlusAffiliations
                          :label="$t('message.international_affiliation')"
                          v-model="affiliation"
                          @input="onAffiliationChange"
                        />
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div
                      v-if="profileExpansionPanel !== 1"
                      class="flex align-center"
                      style="gap: 0.75rem"
                    >
                      <v-icon size="24" color="plusTextSecondary"
                        >mdi-image-outline</v-icon
                      >
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 1rem"
                      >
                        {{
                          $t('message.academy_settings_admin_plus.cover_image')
                        }}
                      </div>
                    </div>
                    <div
                      v-if="profileExpansionPanel === 1"
                      class="flex align-center"
                      style="gap: 0.75rem"
                    >
                      <v-icon size="24" color="plusTextPrimary"
                        >mdi-image-outline</v-icon
                      >
                      <div
                        class="plusTextPrimary--text"
                        style="font-size: 1.25rem"
                      >
                        {{
                          $t('message.academy_settings_admin_plus.cover_image')
                        }}
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <PlusCoverImageUpload
                      :image-url="altAcademyImage"
                      @uploaded="onCoverImageUploaded"
                    />
                    <v-btn
                      block
                      elevation="0"
                      outlined
                      v-if="altAcademyImage"
                      @click="clearAltImage()"
                      color="error"
                      small
                    >
                      <v-icon class="mr-1">mdi-trash-can-outline</v-icon>
                      {{ $t('message.delete') }}
                    </v-btn>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div
                      v-if="profileExpansionPanel !== 2"
                      class="flex align-center"
                      style="gap: 0.75rem"
                    >
                      <v-icon size="24" color="plusTextSecondary"
                        >mdi-palette-outline</v-icon
                      >
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 1rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.academy_colors',
                          )
                        }}
                      </div>
                    </div>
                    <div
                      v-if="profileExpansionPanel === 2"
                      class="flex align-center"
                      style="gap: 0.75rem"
                    >
                      <v-icon size="24" color="plusTextPrimary"
                        >mdi-palette-outline</v-icon
                      >
                      <div
                        class="plusTextPrimary--text"
                        style="font-size: 1.25rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.academy_colors',
                          )
                        }}
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="flex flex-col" style="gap: 2rem">
                      <div class="plusTextSecondary--text">
                        {{
                          $t(
                            'message.academy_settings_admin_plus.academy_colors_explained',
                          )
                        }}
                      </div>
                      <div
                        class="flex justify-space-between plusGreyLighten5 rounded"
                        style="
                          border: 1px solid #e0e0e0 !important;
                          padding: 0.75rem;
                        "
                      >
                        <div class="flex align-center" style="gap: 0.75rem">
                          <div
                            class="rounded"
                            :style="`height:40px;width:40px;border:1px solid #757575;background:${colorTheme.primary}`"
                          ></div>
                          <div class="flex flex-col" style="gap:0.25rem">
                            <div class="plusTextPrimary--text" style="font-size:0.875rem;font-weight:500">
                              {{ $t('message.primary') }}
                            </div>
                            <div
                              class="plusTextSecondary--text"
                              style="font-size:0.75rem"
                            >
                              {{ typeof colorTheme.primary === 'string' ? colorTheme.primary : '' }}
                            </div>
                          </div>
                        </div>
                        <div>
                          <PlusButton @click="showColorEditor('primary')">{{
                            $t('message.pick_color')
                          }}</PlusButton>
                        </div>
                      </div>
                      <div>
                        <v-switch
                          color="plusIndigoLighten1"
                          inset
                          v-model="colorTheme.lightMode"
                          :label="$t('message.light_mode')"
                        ></v-switch>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
              <div class="flex justify-end mt-6">
                <PlusButton
                  @click="saveAcademyInfo()"
                  small
                  intent="primary"
                  >{{ $t('message.update') }}</PlusButton
                >
              </div>
            </div>
          </div>
        </v-tab-item>
        <v-tab-item key="settings">
          <v-expansion-panels v-model="settingsExpansionPanel">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div
                  v-if="settingsExpansionPanel !== 0"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextSecondary"
                    >mdi-tablet-dashboard</v-icon
                  >
                  <div class="plusTextSecondary--text" style="font-size: 1rem">
                    {{
                      $t(
                        'message.academy_settings_admin_plus.tablet_credentials',
                      )
                    }}
                  </div>
                </div>
                <div
                  v-if="settingsExpansionPanel === 0"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextPrimary"
                    >mdi-tablet-dashboard</v-icon
                  >
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{
                      $t(
                        'message.academy_settings_admin_plus.tablet_credentials',
                      )
                    }}
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="flex flex-col" style="gap: 2rem">
                  <div class="plusTextSecondary--text">
                    {{
                      $t(
                        'message.academy_settings_admin_plus.tablet_credentials_explained',
                      )
                    }}
                  </div>
                  <div>
                    <v-text-field
                      dense
                      outlined
                      :label="
                        $t('message.academy_settings_admin_plus.tablet_id')
                      "
                      v-model="academy.tabletCredentials.id"
                    ></v-text-field>
                    <v-text-field
                      dense
                      outlined
                      :label="$t('message.password')"
                      v-model="academy.tabletCredentials.password"
                    >
                    </v-text-field>
                    <div class="flex justify-end" style="gap: 1.25rem">
                      <PlusButton @click="cancelTabletCredentials()">{{
                        $t('message.cancel')
                      }}</PlusButton>
                      <PlusButton
                        intent="primary"
                        @click="updateTabletCredentials()"
                        >{{ $t('message.update') }}
                      </PlusButton>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div
                  v-if="settingsExpansionPanel !== 1"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextSecondary"
                    >mdi-shield-account-variant-outline</v-icon
                  >
                  <div class="plusTextSecondary--text" style="font-size: 1rem">
                    {{
                      $t('message.academy_settings_admin_plus.administrators')
                    }}
                  </div>
                </div>
                <div
                  v-if="settingsExpansionPanel === 1"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextPrimary"
                    >mdi-shield-account-variant-outline</v-icon
                  >
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{
                      $t('message.academy_settings_admin_plus.administrators')
                    }}
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-autocomplete
                  v-allowed="'root'"
                  dense
                  outlined
                  v-model="selectedAdmin"
                  :items="members"
                  :label="$t('message.add_new_admin')"
                  item-text="text"
                  item-value="value"
                  clearable
                ></v-autocomplete>
                <template v-for="(m, index) in managers">
                  <div
                    :key="m._id + index"
                    class="flex justify-space-between w-100"
                    v-if="!isAppAdmin(m._id)"
                  >
                    <v-list-item-avatar>
                      <ImageDisplay
                        v-if="m.userImage || m.userImageSmall"
                        :src="m.userImage || m.userImageSmall"
                        :title="m.displayName"
                      />
                      <v-avatar
                        v-else
                        size="40"
                        color="plusIndigoLighten1"
                      >
                        <span class="white--text" style="font-size:0.875rem;font-weight:600">
                          {{ getInitials(m.displayName) }}
                        </span>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span v-html="m.displayName"></span>
                      </v-list-item-title>
                    </v-list-item-content>
                    <div class="flex flex-grow-0 items-center">
                      <v-btn
                        v-if="m._id !== ($store.state.user && $store.state.user._id)"
                        v-allowed="'root'"
                        @click="removeAdmin(m._id)"
                        small
                        icon
                      >
                        <v-icon color="plusGreyDarken1"
                          >mdi-delete-outline</v-icon
                        >
                      </v-btn>
                      <v-btn
                        :loading="loadingPermissions"
                        v-allowed="'root'"
                        small
                        icon
                        @click="viewAdminPermissions(m._id)"
                      >
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <v-divider
                    v-if="index + 1 < managers.length"
                    :key="index"
                  ></v-divider>
                </template>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header @click="getUpdates">
                <div
                  v-if="settingsExpansionPanel !== 2"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextSecondary"
                    >mdi-history</v-icon
                  >
                  <div class="plusTextSecondary--text" style="font-size: 1rem">
                    {{
                      $t('message.academy_settings_admin_plus.admins_activity')
                    }}
                  </div>
                </div>
                <div
                  v-if="settingsExpansionPanel === 2"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextPrimary">mdi-history</v-icon>
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{
                      $t('message.academy_settings_admin_plus.admins_activity')
                    }}
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="flex flex-col" style="gap: 2rem">
                  <div class="plusTextSecondary--text">
                    {{
                      $t(
                        'message.academy_settings_admin_plus.admins_activity_explained',
                      )
                    }}
                  </div>
                  <div>
                    <v-card-text class="px-0">
                      <v-autocomplete
                        v-model="activityActionFilter"
                        @change="getUpdates"
                        :items="activityActions"
                        outlined
                        dense
                        clearable
                        :label="$t('message.filter')"
                      ></v-autocomplete>
                    </v-card-text>
                    <PlusAcademyActivity
                      v-if="updates && updates.length > 0"
                      :activity="updates"
                    />
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div
                  v-if="settingsExpansionPanel !== 3"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextSecondary"
                    >mdi-list-box-outline</v-icon
                  >
                  <div class="plusTextSecondary--text" style="font-size: 1rem">
                    {{ $t('message.academy_settings_admin_plus.contact_form') }}
                  </div>
                </div>
                <div
                  v-if="settingsExpansionPanel === 3"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextPrimary"
                    >mdi-list-box-outline</v-icon
                  >
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{ $t('message.academy_settings_admin_plus.contact_form') }}
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="flex flex-col" style="gap: 2rem">
                  <div class="plusTextSecondary--text">
                    {{
                      $t(
                        'message.academy_settings_admin_plus.contact_form_explained',
                      )
                    }}
                  </div>
                  <div>
                    <v-switch
                      color="plusIndigoLighten1"
                      inset
                      hide-details
                      @change="updateAcademyConfiguration"
                      v-model="academy.configuration.hideContactFormInput"
                      :label="$t('message.hide_message_input')"
                      class="mt-0"
                    ></v-switch>
                    <v-switch
                      color="plusIndigoLighten1"
                      inset
                      hide-details
                      @change="updateAcademyConfiguration"
                      v-model="academy.configuration.makePhoneOptional"
                      :label="$t('message.make_phone_optional')"
                    ></v-switch>
                    <v-divider class="mt-6"></v-divider>
                  </div>
                  <div class="flex flex-col" style="gap: 2rem">
                    <div class="flex justify-space-between items-center">
                      <div>{{ $t('message.custom_fields') }}</div>
                      <div>
                        <PlusButton
                          outlined
                          intent="primary"
                          small
                          @click="addNewExraField()"
                        >
                          {{
                            $t(
                              'message.academy_settings_admin_plus.add_custom_field',
                            )
                          }}
                        </PlusButton>
                      </div>
                    </div>
                    <div
                      class="flex flex-col"
                      style="gap: 2rem"
                      v-if="contactFormExtraFields.length > 0"
                    >
                      <template
                        v-for="(field, index) in contactFormExtraFields"
                      >
                        <div
                          :key="'contact_form_extra_field_' + index"
                          class="pa-5 rounded"
                          style="border: 1px solid #e0e0e0"
                        >
                          <div class="flex flex-col" style="gap: 1rem">
                            <div
                              class="flex justify-space-between items-center"
                            >
                              <div style="font-size: 1.25rem">
                                {{
                                  $t(
                                    'message.academy_settings_admin_plus.custom_field_title',
                                    {
                                      index: index + 1,
                                      name: field.fieldName ?? '',
                                    },
                                  )
                                }}
                              </div>
                              <div>
                                <v-btn
                                  elevation="0"
                                  small
                                  @click="removeExtraField(index)"
                                >
                                  {{
                                    $t(
                                      'message.academy_settings_admin_plus.delete_field',
                                    )
                                  }}
                                </v-btn>
                              </div>
                            </div>
                            <div>
                              <v-text-field
                                style="max-width: 22rem"
                                outlined
                                hide-details
                                dense
                                :label="$t('message.name')"
                                v-model="field.fieldName"
                              ></v-text-field>
                            </div>
                          </div>
                          <div>
                            <v-checkbox
                              hide-details
                              dense
                              :label="$t('message.required')"
                              v-model="field.required"
                            ></v-checkbox>
                            <v-checkbox
                              hide-details
                              dense
                              :label="$t('message.show_in_contact_form')"
                              v-model="field.showInContactForm"
                            ></v-checkbox>
                            <v-checkbox
                              hide-details
                              dense
                              :label="$t('message.show_in_schedule_class')"
                              v-model="field.showInScheduleClassForm"
                            ></v-checkbox>
                          </div>
                        </div>
                      </template>
                    </div>
                    <div class="flex justify-end">
                      <PlusButton
                        style="min-width: 14.438rem"
                        elevation="0"
                        intent="primary"
                        outlined
                        small
                        @click="updateContactFormExtraFields()"
                      >
                        {{ $t('message.update') }}
                      </PlusButton>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header
                @click="
                  getAvailableEmails();
                  getEmails();
                "
              >
                <div
                  v-if="settingsExpansionPanel !== 4"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextSecondary"
                    >mdi-email-check-outline</v-icon
                  >
                  <div class="plusTextSecondary--text" style="font-size: 1rem">
                    {{ $t('message.academy_settings_admin_plus.emails_sent') }}
                  </div>
                </div>
                <div
                  v-if="settingsExpansionPanel === 4"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextPrimary"
                    >mdi-email-check-outline</v-icon
                  >
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{ $t('message.academy_settings_admin_plus.emails_sent') }}
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content v-if="emails">
                <div class="flex flex-col" style="gap: 2rem">
                  <div class="plusTextSecondary--text">
                    {{
                      $t(
                        'message.academy_settings_admin_plus.emails_sent_explained',
                      )
                    }}
                  </div>
                  <div>
                    <v-card-text class="px-0 py-0">
                      <v-autocomplete
                        v-if="availableEmails"
                        @input="searchEmail"
                        v-model="selectedEmail"
                        :items="availableEmails"
                        :label="$t('message.search_by_email')"
                        clearable
                        outlined
                        dense
                      >
                      </v-autocomplete>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        :label="$t('message.failed_emails_only')"
                        @change="searchEmail"
                        v-model="failedEmailsFilter"
                      ></v-switch>
                      <v-switch
                        color="plusIndigoDarken4"
                        inset
                        :label="$t('message.automation_emails_only')"
                        @change="searchEmail"
                        v-model="automationEmailsFilter"
                      ></v-switch>
                    </v-card-text>
                    <div
                      v-if="!selectedEmail && (!emails || emails.length == 0)"
                      class="text-center text-cubtitle-1 primary--text"
                    >
                      {{ $t('message.no_emails') }}
                    </div>
                    <v-skeleton-loader
                      v-if="loadingEmails"
                      type="list-item-avatar, divider, list-item-avatar, divider, list-item-avatar"
                    ></v-skeleton-loader>
                    <div class="text-caption" v-else-if="emails.length > 50">
                      {{ $t('message.showing_first_results_only', [50]) }}
                    </div>
                    <template v-for="(e, index) in emails">
                      <v-list-item
                        two-line
                        :key="'update' + index"
                        class="px-0 text-left"
                        @click="getEmail(e)"
                      >
                        <v-list-item-content>
                          <v-list-item-title>
                            <span v-if="e.opening">
                              <v-progress-circular
                                size="15"
                                width="5"
                                indeterminate
                                color="secondary"
                              ></v-progress-circular>
                            </span>
                            <span v-else>{{ e.subject }}</span>
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ $d(new Date(e.time), 'short', $i18n.locale) }}
                            <v-icon
                              class="ml-1"
                              size="20"
                              v-if="e.failed"
                              color="error"
                              >mdi-email-alert-outline</v-icon
                            >
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-badge color="secondary" :content="e.recipients">
                            <v-icon size="20" v-if="e.recipients == 1"
                              >mdi-account-outline</v-icon
                            >
                            <v-icon size="20" v-else-if="e.recipients == 2"
                              >mdi-account-multiple-outline</v-icon
                            >
                            <v-icon size="20" v-else
                              >mdi-account-group-outline</v-icon
                            >
                          </v-badge>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider
                        v-if="index + 1 < emails.length"
                        :key="index"
                      ></v-divider>
                    </template>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div
                  v-if="settingsExpansionPanel !== 5"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextSecondary"
                    >mdi-delete-sweep-outline</v-icon
                  >
                  <div class="plusTextSecondary--text" style="font-size: 1rem">
                    {{
                      $t('message.academy_settings_admin_plus.removed_emails')
                    }}
                  </div>
                </div>
                <div
                  v-if="settingsExpansionPanel === 5"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextPrimary"
                    >mdi-delete-sweep-outline</v-icon
                  >
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{
                      $t('message.academy_settings_admin_plus.removed_emails')
                    }}
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="flex" style="gap: 1rem">
                  <v-text-field
                    outlined
                    v-model="unsubscribedEmail"
                    :label="$t('message.email')"
                  ></v-text-field>
                  <v-btn
                    class="py-6"
                    elevation="0"
                    :disabled="
                      !unsubscribedEmail || unsubscribedEmail.indexOf('@') < 0
                    "
                    icon
                    color="primary"
                    small
                    @click="addUnsubscribedEmail()"
                  >
                    <v-icon>mdi-plus-circle-outline</v-icon>
                  </v-btn>
                </div>
                <template v-for="(e, index) in academy.unsubscribedEmails">
                  <v-list-item
                    two-line
                    :key="'unsubscribed_email' + index"
                    class="text-left"
                    @click="goToUnsubscribedEmailUser(e)"
                  >
                    <v-list-item-action>
                      <v-icon color="plusGreyDarken1" v-if="e.user"
                        >mdi-card-account-mail-outline</v-icon
                      >
                      <v-icon color="plusGreyDarken1" v-else>mdi-at</v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ e.email }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="plusTextSecondary--text">
                        {{ $t('message.added_on') }}:
                        {{ $d(new Date(e.addedOn), 'short', $i18n.locale) }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle
                        class="plusTextSecondary--text"
                        v-if="e.publicForm"
                      >
                        {{ $t('message.public_form_submission') }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        icon
                        @click.stop="removeUnsubscribedEmail(e.email)"
                      >
                        <v-icon color="plusGreyDarken1"
                          >mdi-minus-circle-outline</v-icon
                        >
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider
                    v-if="index + 1 < academy.unsubscribedEmails"
                    :key="index"
                  ></v-divider>
                </template>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header @click="getEmailTemplates()">
                <div
                  v-if="settingsExpansionPanel !== 6"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextSecondary"
                    >mdi-email-sync-outline</v-icon
                  >
                  <div class="plusTextSecondary--text" style="font-size: 1rem">
                    {{
                      $t('message.academy_settings_admin_plus.automated_emails')
                    }}
                  </div>
                </div>
                <div
                  v-if="settingsExpansionPanel === 6"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextPrimary"
                    >mdi-email-sync-outline</v-icon
                  >
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{
                      $t('message.academy_settings_admin_plus.automated_emails')
                    }}
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content v-if="academy.automatedEmails">
                <div class="plusTextSecondary--text">
                  {{
                    $t(
                      'message.academy_settings_admin_plus.automated_emails_explained',
                    )
                  }}
                </div>
                <div class="pt-8 pb-6 flex flex-col" style="gap: 1rem">
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{
                      $t(
                        'message.academy_settings_admin_plus.daily_association_alerts',
                      )
                    }}
                  </div>
                  <v-switch
                    color="plusIndigoLighten1"
                    inset
                    :label="$t('message.active')"
                    :hide-details="true"
                    class="pt-0 mt-0"
                    v-model="
                      academy.automatedEmails['dailyMembershipAlerts'].active
                    "
                  ></v-switch>
                  <div
                    v-if="
                      academy.automatedEmails['dailyMembershipAlerts'].emails
                        .length
                    "
                  >
                    <div class="flex flex-col" style="gap: 1rem">
                      <span class="plusTextPrimary--text"
                        >{{ $t('message.alternative_emails') }}:</span
                      >
                      <div>
                        <template
                          v-for="(i, index) in academy.automatedEmails[
                            'dailyMembershipAlerts'
                          ].emails"
                        >
                          <v-chip
                            :close="true"
                            @click:close="
                              removeAlternativeEmail('dailyMembershipAlerts', i)
                            "
                            :key="'alt_automation_email' + index"
                            v-if="i && i != ''"
                            small
                            class="ma-1 plusDeepPurpleLighten5 plusTextPrimary--text font-weight-regular"
                            style="font-size: 0.875rem"
                            >{{ i }}</v-chip
                          >
                        </template>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center" style="gap: 1.25rem">
                    <v-text-field
                      :hide-details="true"
                      outlined
                      dense
                      :label="$t('message.add_alternative_email')"
                      v-model="newAlternativeEmails['dailyMembershipAlerts']"
                    ></v-text-field>
                    <v-btn
                      @click="addAlternativeEmail('dailyMembershipAlerts')"
                    >
                      {{ $t('message.academy_settings_admin_plus.add_email') }}
                    </v-btn>
                  </div>
                </div>
                <v-divider></v-divider>
                <div
                  class="pt-8 pb-6 flex flex-col"
                  style="gap: 1rem"
                  v-if="academy.automatedEmails['dailyMembershipAlerts'].active"
                >
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{ $t('message.weekly_members_update') }}
                  </div>
                  <v-switch
                    color="plusIndigoLighten1"
                    inset
                    :label="$t('message.active')"
                    :hide-details="true"
                    class="pt-0 mt-0"
                    v-model="
                      academy.automatedEmails['weeklyMembersUpdate'].active
                    "
                  ></v-switch>
                  <div
                    v-if="
                      academy.automatedEmails['weeklyMembersUpdate'].emails
                        .length
                    "
                  >
                    <div class="flex flex-col" style="gap: 1rem">
                      <span class="plusTextPrimary--text"
                        >{{ $t('message.alternative_emails') }}:</span
                      >
                      <div>
                        <template
                          v-for="(i, index) in academy.automatedEmails[
                            'weeklyMembersUpdate'
                          ].emails"
                        >
                          <v-chip
                            close
                            @click:close="
                              removeAutomatedEmail('weeklyMembersUpdate', i)
                            "
                            :key="'alt_automation_email' + index"
                            v-if="i && i != ''"
                            small
                            class="ma-1 plusDeepPurpleLighten5 plusTextPrimary--text font-weight-regular"
                            style="font-size: 0.875rem"
                            >{{ i }}</v-chip
                          >
                        </template>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center" style="gap: 1.25rem">
                    <v-text-field
                      :hide-details="true"
                      outlined
                      dense
                      :label="$t('message.add_alternative_email')"
                      v-model="newAlternativeEmails['weeklyMembersUpdate']"
                    ></v-text-field>
                    <v-btn @click="addAlternativeEmail('weeklyMembersUpdate')">
                      {{ $t('message.academy_settings_admin_plus.add_email') }}
                    </v-btn>
                  </div>
                  <v-divider></v-divider>
                </div>
                <div class="pt-8 flex flex-col" style="gap: 1rem">
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{ $t('message.purchase_confirmations') }}
                  </div>
                  <v-switch
                    color="plusIndigoLighten1"
                    inset
                    :label="$t('message.active')"
                    :hide-details="true"
                    class="pt-0 mt-0"
                    v-model="
                      academy.automatedEmails['purchaseConfirmations'].active
                    "
                  ></v-switch>
                  <div
                    v-if="
                      academy.automatedEmails['purchaseConfirmations'].emails
                        .length
                    "
                  >
                    <div class="flex flex-col" style="gap: 1rem">
                      <span class="plusTextPrimary--text"
                        >{{ $t('message.alternative_emails') }}:</span
                      >
                      <div>
                        <template
                          v-for="(i, index) in academy.automatedEmails[
                            'purchaseConfirmations'
                          ].emails"
                        >
                          <v-chip
                            close
                            close-icon="mdi-delete"
                            @click:close="
                              removeAutomatedEmail('purchaseConfirmations', i)
                            "
                            :key="'alt_automation_email' + index"
                            v-if="i && i != ''"
                            small
                            class="ma-1 plusDeepPurpleLighten5 plusTextPrimary--text font-weight-regular"
                            style="font-size: 0.875rem"
                            >{{ i }}</v-chip
                          >
                        </template>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center" style="gap: 1rem">
                    <v-text-field
                      :hide-details="true"
                      outlined
                      dense
                      :label="$t('message.add_alternative_email')"
                      v-model="newAlternativeEmails['purchaseConfirmations']"
                    ></v-text-field>
                    <v-btn
                      @click="addAlternativeEmail('purchaseConfirmations')"
                    >
                      {{ $t('message.academy_settings_admin_plus.add_email') }}
                    </v-btn>
                  </div>
                  <div style="max-width: 15.5rem">
                    <v-select
                      item-text="name"
                      item-value="_id"
                      outlined
                      dense
                      :clearable="true"
                      v-model="
                        academy.automatedEmails['newVisitSubmissions'].template
                      "
                      :placeholder="$t('message.select_email_template')"
                      :items="emailTemplates"
                    >
                    </v-select>
                  </div>
                </div>
                <v-divider></v-divider>
                <div class="pt-8 flex flex-col" style="gap: 1rem">
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{
                      $t(
                        'message.academy_settings_admin_plus.new_visits_submissions',
                      )
                    }}
                  </div>
                  <div
                    v-if="
                      academy.automatedEmails['newVisitSubmissions'].emails
                        .length
                    "
                  >
                    <div class="flex flex-col" style="gap: 1rem">
                      <span class="plusTextPrimary--text"
                        >{{ $t('message.alternative_emails') }}:</span
                      >
                      <div>
                        <template
                          v-for="(i, index) in academy.automatedEmails[
                            'newVisitSubmissions'
                          ].emails"
                        >
                          <v-chip
                            @click:close="
                              removeAutomatedEmail('newVisitSubmissions', i)
                            "
                            close
                            close-icon="mdi-delete"
                            :key="'alt_automation_email' + index"
                            v-if="i && i != ''"
                            small
                            class="ma-1 plusDeepPurpleLighten5 plusTextPrimary--text font-weight-regular"
                            style="font-size: 0.875rem"
                            >{{ i }}</v-chip
                          >
                        </template>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center" style="gap: 1rem">
                    <v-text-field
                      :hide-details="true"
                      outlined
                      dense
                      :label="$t('message.add_alternative_email')"
                      v-model="newAlternativeEmails['newVisitSubmissions']"
                    ></v-text-field>
                    <v-btn @click="addAlternativeEmail('newVisitSubmissions')">
                      {{ $t('message.academy_settings_admin_plus.add_email') }}
                    </v-btn>
                  </div>
                  <div style="max-width: 15.5rem">
                    <v-select
                      item-text="name"
                      item-value="_id"
                      outlined
                      dense
                      :clearable="true"
                      v-model="
                        academy.automatedEmails['newVisitSubmissions'].template
                      "
                      :placeholder="$t('message.select_email_template')"
                      :items="emailTemplates"
                    >
                    </v-select>
                  </div>
                </div>
                <v-divider></v-divider>
                <div class="pt-8 flex flex-col" style="gap: 1rem">
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{ $t('message.inquiry_submissions') }}
                  </div>
                  <div
                    class="plusTextSecondary--text"
                    style="font-size: 0.875rem"
                  >
                    {{ $t('message.inquiry_submissions_explained') }}
                  </div>
                  <v-switch
                    color="plusIndigoLighten1"
                    inset
                    :label="$t('message.active')"
                    :hide-details="true"
                    class="pt-0 mt-0"
                    v-model="
                      academy.automatedEmails['inquirySubmissions'].active
                    "
                  ></v-switch>
                  <div
                    v-if="
                      academy.automatedEmails['inquirySubmissions'].emails
                        .length
                    "
                  >
                    <div class="flex flex-col" style="gap: 1rem">
                      <span class="mr-1 text-subtitle-2"
                        >{{ $t('message.alternative_emails') }}:</span
                      >
                      <div>
                        <template
                          v-for="(i, index) in academy.automatedEmails[
                            'inquirySubmissions'
                          ].emails"
                        >
                          <v-chip
                            close
                            close-icon="mdi-delete"
                            @click:close="
                              removeAutomatedEmail('inquirySubmissions', i)
                            "
                            :key="'alt_automation_email' + index"
                            v-if="i && i != ''"
                            small
                            class="ma-1 plusDeepPurpleLighten5 plusTextPrimary--text font-weight-regular"
                            style="font-size: 0.875rem"
                            >{{ i }}</v-chip
                          >
                        </template>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center" style="gap: 1rem">
                    <v-text-field
                      :hide-details="true"
                      outlined
                      dense
                      :label="$t('message.add_alternative_email')"
                      v-model="newAlternativeEmails['inquirySubmissions']"
                    ></v-text-field>
                    <v-btn @click="addAlternativeEmail('inquirySubmissions')">
                      {{ $t('message.academy_settings_admin_plus.add_email') }}
                    </v-btn>
                  </div>
                  <div style="max-width: 15.5rem">
                    <v-select
                      item-text="name"
                      item-value="_id"
                      outlined
                      dense
                      :clearable="true"
                      v-model="
                        academy.automatedEmails['inquirySubmissions'].template
                      "
                      :placeholder="$t('message.select_email_template')"
                      :items="emailTemplates"
                    >
                    </v-select>
                  </div>
                </div>
                <v-divider></v-divider>
                <div
                  class="pt-8 pb-6 flex flex-col"
                  style="gap: 1rem"
                  v-if="academy.automatedEmails['birthdayCelebration']"
                >
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{ $t('message.birthday_celebration') }}
                  </div>
                  <div
                    class="plusTextSecondary--text"
                    style="font-size: 0.875rem"
                  >
                    {{ $t('message.birthday_celebration_explained') }}
                  </div>
                  <v-switch
                    color="plusIndigoLighten1"
                    inset
                    :label="$t('message.active')"
                    :hide-details="true"
                    class="pt-0 mt-0"
                    v-model="
                      academy.automatedEmails['birthdayCelebration'].active
                    "
                  ></v-switch>
                  <div
                    style="max-width: 15.5rem"
                    v-if="academy.automatedEmails['birthdayCelebration'].active"
                  >
                    <v-select
                      item-text="name"
                      item-value="_id"
                      outlined
                      dense
                      :clearable="true"
                      v-model="
                        academy.automatedEmails['birthdayCelebration'].template
                      "
                      :placeholder="$t('message.select_email_template')"
                      :items="emailTemplates"
                    >
                    </v-select>
                  </div>
                </div>
                <v-divider></v-divider>
                <div class="flex justify-end mt-4">
                  <PlusButton
                    @click="updateAlternateEmails()"
                    small
                    intent="primary"
                    style="min-width: 14.438rem"
                    >{{ $t('message.update') }}</PlusButton
                  >
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div
                  v-if="settingsExpansionPanel !== 7"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextSecondary"
                    >mdi-cog-outline</v-icon
                  >
                  <div class="plusTextSecondary--text" style="font-size: 1rem">
                    {{
                      $t('message.academy_settings_admin_plus.configuration')
                    }}
                  </div>
                </div>
                <div
                  v-if="settingsExpansionPanel === 7"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextPrimary"
                    >mdi-cog-outline</v-icon
                  >
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{
                      $t('message.academy_settings_admin_plus.configuration')
                    }}
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="flex flex-col" style="gap: 2rem">
                  <div class="plusTextSecondary--text">
                    {{
                      $t(
                        'message.academy_settings_admin_plus.configuration_explained',
                      )
                    }}
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.disableSocialPodium"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.disableSocialPodium,
                          'plusTextSecondary--text':
                            !academy.configuration.disableSocialPodium,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.disable_podium_social',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.disable_podium_social_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.tabletCheckinConfirm"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.tabletCheckinConfirm,
                          'plusTextSecondary--text':
                            !academy.configuration.tabletCheckinConfirm,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.checkin_confirmation_tablet',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.checkin_confirmation_tablet_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.privateAcademyFeed"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.privateAcademyFeed,
                          'plusTextSecondary--text':
                            !academy.configuration.privateAcademyFeed,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.enable_private_wall',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.enable_private_wall_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.altTabletCheckin"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.altTabletCheckin,
                          'plusTextSecondary--text':
                            !academy.configuration.altTabletCheckin,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.alternative_tablet_checkin',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.alternative_tablet_checkin_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.blockMissingDocCheckins"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.blockMissingDocCheckins,
                          'plusTextSecondary--text':
                            !academy.configuration.blockMissingDocCheckins,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.block_checkins_missing_documents',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.block_checkins_missing_documents_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.restrictClassReservation"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.restrictClassReservation,
                          'plusTextSecondary--text':
                            !academy.configuration.restrictClassReservation,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.restrict_class_reservation_explained',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.restrict_class_reservation_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.displayMembershipStatus"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.displayMembershipStatus,
                          'plusTextSecondary--text':
                            !academy.configuration.displayMembershipStatus,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.display_membership_status_checkin',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.display_membership_status_checkin_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="
                          academy.configuration.disableSubscriptionAlerts
                        "
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.disableSubscriptionAlerts,
                          'plusTextSecondary--text':
                            !academy.configuration.disableSubscriptionAlerts,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.disable_subscription_alerts',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.disable_subscription_alerts_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.useSyllabusRanks"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.useSyllabusRanks,
                          'plusTextSecondary--text':
                            !academy.configuration.useSyllabusRanks,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.use_syllabus_levels',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.use_syllabus_levels_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="
                          academy.configuration
                            .hideCurriculumProgressInMembersList
                        "
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration
                              .hideCurriculumProgressInMembersList,
                          'plusTextSecondary--text':
                            !academy.configuration
                              .hideCurriculumProgressInMembersList,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.hide_curriculum_progress_in_members_list',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.hide_curriculum_progress_in_members_list_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.enableExpiredMembershipAlerts"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.enableExpiredMembershipAlerts,
                          'plusTextSecondary--text':
                            !academy.configuration.enableExpiredMembershipAlerts,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.enable_expired_membership_alerts',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.enable_expired_membership_alerts_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.hidePauseRequestButton"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.hidePauseRequestButton,
                          'plusTextSecondary--text':
                            !academy.configuration.hidePauseRequestButton,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.hide_pause_request_button',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.hide_pause_request_button_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="
                          academy.configuration.hideCancellationRequestButton
                        "
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.hideCancellationRequestButton,
                          'plusTextSecondary--text':
                            !academy.configuration
                              .hideCancellationRequestButton,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.hide_cancellation_request_button',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.hide_cancellation_request_button_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex" style="gap: 0.75rem">
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        @change="updateAcademyConfiguration"
                        v-model="academy.configuration.hideMembersInSocial"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.configuration.hideMembersInSocial,
                          'plusTextSecondary--text':
                            !academy.configuration.hideMembersInSocial,
                        }"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.hide_members_bjjlink_connect',
                          )
                        }}
                      </div>
                      <div
                        class="plusTextSecondary--text"
                        style="font-size: 0.875rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.hide_members_bjjlink_connect_explained',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-col" style="gap: 1.5rem">
                    <div class="flex flex-col" style="gap: 0.5rem">
                      <div
                        class="plusTextPrimary--text"
                        style="font-size: 1.25rem"
                      >
                        {{
                          $t(
                            'message.academy_settings_admin_plus.subscription_reminder_interval',
                          )
                        }}
                      </div>
                      <div class="plusTextPrimary--text">
                        {{
                          $t(
                            'message.academy_settings_admin_plus.subscription_reminder_interval_explained',
                          )
                        }}
                      </div>
                    </div>
                    <div class="flex" style="gap: 1.25rem">
                      <v-text-field
                        style="max-width: 17rem"
                        clearable
                        @change="updateAcademyConfiguration"
                        dense
                        hide-details
                        outlined
                        v-model="
                          academy.configuration.membershipsRemindersInterval
                        "
                      ></v-text-field>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div
                  v-if="settingsExpansionPanel !== 8"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextSecondary"
                    >mdi-account-alert-outline</v-icon
                  >
                  <div class="plusTextSecondary--text" style="font-size: 1rem">
                    {{ $t('message.required_profile_fields') }}
                  </div>
                </div>
                <div
                  v-if="settingsExpansionPanel === 8"
                  class="flex align-center"
                  style="gap: 0.75rem"
                >
                  <v-icon size="24" color="plusTextPrimary"
                    >mdi-account-alert-outline</v-icon
                  >
                  <div class="plusTextPrimary--text" style="font-size: 1.25rem">
                    {{ $t('message.required_profile_fields') }}
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content
                v-if="academy.requiredPrivateInfoFields"
              >
                <div class="plusTextSecondary--text pb-6">
                  {{ $t('message.required_profile_fields_explained') }}
                </div>
                <div class="flex flex-col" style="gap: 1.25rem">
                  <div
                    class="flex"
                    style="gap: 0.75rem"
                    v-for="f in requiredPrivateFieldsDefs"
                    :key="f.key"
                  >
                    <div>
                      <v-switch
                        color="plusIndigoLighten1"
                        inset
                        class="mt-0"
                        hide-details
                        @change="updateRequiredPrivateInfoFields"
                        v-model="academy.requiredPrivateInfoFields[f.key]"
                      ></v-switch>
                    </div>
                    <div>
                      <div
                        :class="{
                          'plusTextPrimary--text':
                            academy.requiredPrivateInfoFields[f.key],
                          'plusTextSecondary--text':
                            !academy.requiredPrivateInfoFields[f.key],
                        }"
                      >
                        {{ $t('message.' + f.label) }}
                      </div>
                    </div>
                  </div>
                </div>
                <v-divider class="my-6"></v-divider>
                <div
                  class="flex"
                  style="gap: 0.75rem"
                  v-if="
                    academy.automatedEmails &&
                    academy.automatedEmails.missingInfoReminders
                  "
                >
                  <div>
                    <v-switch
                      color="plusIndigoLighten1"
                      inset
                      class="mt-0"
                      hide-details
                      @change="updateMissingInfoReminders"
                      v-model="
                        academy.automatedEmails.missingInfoReminders.active
                      "
                    ></v-switch>
                  </div>
                  <div>
                    <div
                      :class="{
                        'plusTextPrimary--text':
                          academy.automatedEmails.missingInfoReminders.active,
                        'plusTextSecondary--text':
                          !academy.automatedEmails.missingInfoReminders.active,
                      }"
                    >
                      {{ $t('message.missing_info_daily_reminder') }}
                    </div>
                    <div
                      class="plusTextSecondary--text"
                      style="font-size: 0.875rem"
                    >
                      {{ $t('message.missing_info_daily_reminder_explained') }}
                    </div>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-tab-item>
      </v-tabs-items>
    </div>
    <v-dialog v-model="colorEditorDialog" width="auto">
      <v-card outlined>
        <v-card-title>
          {{ $t('message.academy_colors') }}
        </v-card-title>
        <v-card-text>
          <v-color-picker
            v-if="colorTheme && colorTheme.currentEditing"
            :value="colorTheme[colorTheme.currentEditing]"
            @input="onColorPickerInput"
            mode="hexa"
            hide-mode-switch
            dot-size="25"
            swatches-max-height="200"
          ></v-color-picker>
        </v-card-text>
        <v-card-actions>
          <v-btn elevation="0" small @click="colorEditorDialog = null">
            {{ $t('message.close') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            outlined
            elevation="0"
            @click="colorEditorDialog = false"
            small
            color="primary"
          >
            {{ $t('message.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="permissionsDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar flat class="opposite--text" color="primary" dense>
          <v-toolbar-title>
            {{ $t('message.admin_permissions') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn color="opposite" mall icon @click="permissionsDialog = false">
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <div class="pa-1">
          <v-alert type="warning" class="text-caption pa-1">{{
            $t('message.no_permissions_explained')
          }}</v-alert>
        </div>
        <v-card-text>
          <v-text-field
            hide-details
            dense
            outlined
            clearable
            v-model="permissionsFilter"
            :label="$t('message.filter')"
          ></v-text-field>
        </v-card-text>
        <div v-if="selectedPermissions" class="px-4">
          <template v-for="(i, index) in filteredPermissions">
            <div
              :key="'perm_' + index"
              style="min-height:56px;display:flex;align-items:center;"
            >
              <v-switch
                color="plusIndigoLighten1"
                inset
                hide-details
                class="mt-0 pt-0"
                @change="updateAdminPermissions"
                v-model="selectedPermissions[i.field]"
                :label="i.text"
              ></v-switch>
            </div>
          </template>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="emailDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card outlined>
        <v-toolbar dense flat class="opposite--text" color="primary">
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="
              emailDialog = false;
              email = null;
            "
          >
            <v-icon color="opposite">mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card v-if="email" class="pa-2">
          <v-card-actions style="color: gray">
            <v-avatar size="20">
              <ImageDisplay
                v-if="email.sender && email.sender.userImageSmall"
                :src="email.sender.userImageSmall"
              />
              <img v-else :src="assets('default-profile-avatar.png')" />
            </v-avatar>
            <span v-if="email.sender" class="ml-1">
              {{ email.sender.displayName }}
            </span>
            <v-spacer></v-spacer>
            {{ $d(new Date(email.time), 'short', $i18n.locale) }}
          </v-card-actions>
          <v-card-title v-if="email.error" class="text-caption error--text">
            {{ email.error }}
          </v-card-title>
          <v-card class="pa-2" outlined>
            <v-card-title>
              <span class="text-subtitle-1"
                >{{ $t('message.recipients') }}:</span
              >
              <template v-for="(r, index) in email.recipients">
                <v-chip
                  :key="'recipient' + index"
                  v-if="r && r != ''"
                  small
                  class="ma-1"
                  >{{ r }}</v-chip
                >
              </template>
            </v-card-title>
            <v-card-title>
              <span class="text-subtitle-1">{{ $t('message.subject') }}:</span>
              <span style="margin-left: 5px">{{ email.subject }}</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text v-html="email.contents"></v-card-text>
          </v-card>
          <v-card
            outlined
            class="mt-1"
            v-if="email.openedBy && email.openedBy.length"
          >
            <v-card-title>
              <span class="text-subtitle-1"
                >{{ $t('message.opened_by') }}:</span
              >
            </v-card-title>
            <v-list>
              <v-list-item
                dense
                v-for="i in email.openedBy"
                :key="'opened_email_' + i._id"
                @click="doNothing()"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ i.user ? i.user.email : i.email }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $d(new Date(i.time), 'long', $i18n.locale) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-card>
      </v-card>
    </v-dialog>
    <CustomOnboarding
      ref="academySettingsTour"
      tour-name="academy-settings-onboarding"
      :steps="academySettingsTourSteps"
      :finish-route="null"
      :finish-button-text="
        $t('message.academy_settings_admin_plus.onboarding_finish')
      "
    />
  </div>
</template>

<script>
import { Environment } from '@/services/Environment';
import Api from '@/services/Api';
import Api2 from '@/services/Api2';
import { completeOnboardingStep } from '@/services/onboardingSteps';
import PlusImageUploadCroppie from '@/components/plus/PlusImageUploadCroppie';
import PlusCoverImageUpload from '@/components/plus/PlusCoverImageUpload';
import PlusButton from '@/components/plus/PlusButton';
import PlusAcademyActivity from '@/components/plus/PlusAcademyActivity';
import PlusAffiliations from '@/components/plus/PlusAffiliationsCombo.vue';
import Locations from '@/components/inputs/LocationsAutocomplete.vue';
import CustomOnboarding from '@/components/onboarding/CustomOnboarding.vue';
import { buildAcademySettingsTourSteps } from '@/components/onboarding/academySettingsTourSteps';
import cloneDeep from 'lodash/cloneDeep';

export default {
  components: {
    PlusImageUploadCroppie,
    PlusCoverImageUpload,
    PlusButton,
    PlusAcademyActivity,
    PlusAffiliations,
    Locations,
    CustomOnboarding,
  },
  data() {
    return {
      activeTab: 0,
      stripe_client_id: Environment.get('VUE_APP_STRIPE_CLIENT_ID'),
      scope: this.$route.query.scope,
      code: this.$route.query.code,
      stripe_state: null,
      loading: true,
      academy: null,
      savedAcademy: null,
      affiliation: null,
      selectedAdmin: null,
      paymentsReady: false,
      geolocatedAddress: null,
      members: [],
      managers: [],
      localAffiliation: null,
      altStripeAccountDialog: null,
      failedEmailsFilter: false,
      automationEmailsFilter: false,
      updates: null,
      emailDialog: false,
      loadingEmails: false,
      pairingError: null,
      stripeReady: false,
      emails: [],
      emailServices: [
        {
          text: this.$t(
            'message.academy_settings_admin_plus.email_service_google_mail',
          ),
          value: 'gmail',
        },
        {
          text: this.$t(
            'message.academy_settings_admin_plus.email_service_outlook',
          ),
          value: 'outlook',
        },
      ],
      availableEmails: [],
      selectedEmail: null,
      contactFormExtraFields: [],
      allPermissions: [],
      filteredPermissions: [],
      email: null,
      location: null,
      loadingPermissions: false,
      permissionsFilter: null,
      activityActions: null,
      permissionsDialog: null,
      colorEditorDialog: null,
      selectedPermissions: null,
      unsubscribedEmail: null,
      altAcademyImage: null,
      uploadedAltImageFile: null,
      uploading: false,
      activityActionFilter: null,
      hasEmailServer: false,
      allEmails: null,
      readerLabel: null,
      registrationCode: null,
      terminals: [],
      emailTemplates: [],
      pairing: false,
      newStripe: {},
      altStripeAccounts: null,
      readerStripeAccount: null,
      selectedTerminal: null,
      altImageHeight: 'height:350px;',
      colorTheme: {
        primary: this.themeColor('primary'),
        secondary: this.themeColor('secondary'),
        lightMode: true,
        currentEditing: null,
      },
      validPermissions: [
        'root',
        'approve_member',
        'reject_member',
        'create_waiver',
        'tablet_credentials',
        'view_updates',
        'edit_schedule',
        'edit_calendar',
        'add_products',
        'edit_products',
        'sell_items',
        'view_purchases',
        'view_subscriptions',
        'view_income',
        'issue_refund',
        'edit_subscription',
        'create_event',
      ],
      academyImage: '/logo-circle.png',
      newAlternativeEmails: {},
      profileExpansionPanel: 0,
      settingsExpansionPanel: 0,
      requiredPrivateFieldsDefs: [
        { key: 'profileImage', label: 'required_field_profileImage' },
        { key: 'address', label: 'required_field_address' },
        { key: 'phone', label: 'required_field_phone' },
        { key: 'emergencyContact', label: 'required_field_emergencyContact' },
        { key: 'guardian', label: 'required_field_guardian' },
        { key: 'guardianEmail', label: 'required_field_guardianEmail' },
        { key: 'dob', label: 'required_field_dob' },
      ],
      onboardingStepLoaded: false,
      onboardingStepComplete: false,
    };
  },
  created() {
    this.checkAcademyStatus();
    Api2()
      .get('admins/academy/' + this.$store.state.managedAcademy + '/admins')
      .then((response) => {
        this.managers = response.data;
      });
    Api2()
      .get(
        'admins/academy/' +
          this.$store.state.selectedAcademy +
          '?populate=unsubscribed_emails',
      )
      .then((response) => {
        let tmpAcademy = response.data;
        if (tmpAcademy.emailServer) {
          this.hasEmailServer = true;
        }
        this.paymentsReady = tmpAcademy.paymentsReady;
        if (tmpAcademy.contactFormExtraFields)
          this.contactFormExtraFields = tmpAcademy.contactFormExtraFields;
        if (!tmpAcademy.configuration) tmpAcademy.configuration = {};
        if (
          typeof tmpAcademy.configuration.enableProfileImageAlerts ==
          'undefined'
        )
          tmpAcademy.configuration.enableProfileImageAlerts = true;
        if (typeof tmpAcademy.configuration.enableDocumentAlerts == 'undefined')
          tmpAcademy.configuration.enableDocumentAlerts = true;
        if (typeof tmpAcademy.configuration.enableContractAlerts == 'undefined')
          tmpAcademy.configuration.enableContractAlerts = true;
        if (
          typeof tmpAcademy.configuration.enableMembershipAlerts == 'undefined'
        )
          tmpAcademy.configuration.enableMembershipAlerts = true;
        if (typeof tmpAcademy.configuration.disableSocialPodium == 'undefined')
          tmpAcademy.configuration.disableSocialPodium = false;
        if (
          typeof tmpAcademy.configuration.disableSubscriptionAlerts ==
          'undefined'
        )
          tmpAcademy.configuration.disableSubscriptionAlerts = false;
        if (
          typeof tmpAcademy.configuration.automaticTaxCalculation == 'undefined'
        )
          tmpAcademy.configuration.automaticTaxCalculation = false;
        if (
          typeof tmpAcademy.configuration.restrictClassReservation ==
          'undefined'
        )
          tmpAcademy.configuration.restrictClassReservation = false;
        if (typeof tmpAcademy.configuration.altTabletCheckin == 'undefined')
          tmpAcademy.configuration.altTabletCheckin = false;
        if (typeof tmpAcademy.configuration.tabletCheckinConfirm == 'undefined')
          tmpAcademy.configuration.tabletCheckinConfirm = false;
        if (typeof tmpAcademy.configuration.privateAcademyFeed == 'undefined')
          tmpAcademy.configuration.privateAcademyFeed = false;
        if (
          typeof tmpAcademy.configuration.displayMembershipStatus == 'undefined'
        )
          tmpAcademy.configuration.displayMembershipStatus = false;
        if (!tmpAcademy.requiredPrivateInfoFields)
          tmpAcademy.requiredPrivateInfoFields = {};
        const requiredFieldKeys = [
          'profileImage',
          'address',
          'phone',
          'emergencyContact',
          'guardian',
          'guardianEmail',
          'dob',
        ];
        for (const k of requiredFieldKeys) {
          if (typeof tmpAcademy.requiredPrivateInfoFields[k] == 'undefined')
            tmpAcademy.requiredPrivateInfoFields[k] = k == 'profileImage';
        }
        if (!tmpAcademy.automatedEmails) tmpAcademy.automatedEmails = {};
        if (!tmpAcademy.automatedEmails.missingInfoReminders)
          tmpAcademy.automatedEmails.missingInfoReminders = { active: false };
        if (!tmpAcademy.automatedEmails.birthdayCelebration)
          tmpAcademy.automatedEmails.birthdayCelebration = {
            active: false,
            template: null,
          };
        if (tmpAcademy.colorTheme) this.colorTheme = tmpAcademy.colorTheme;
        if (tmpAcademy.description == null) tmpAcademy.description = '';
        this.localAffiliation = tmpAcademy.localAffiliation;
        this.affiliation = tmpAcademy.affiliation;
        this.academyImage = tmpAcademy.image;
        this.altAcademyImage = tmpAcademy.altImage;
        Api2()
          .get(
            'admins/academy/' + this.$store.state.selectedAcademy + '/publicid',
          )
          .then((response) => {
            tmpAcademy.identifier = response.data.identifier;
            this.academy = tmpAcademy;
            this.savedAcademy = cloneDeep(tmpAcademy);
            this.loading = false;
          })
          .catch((e) => {
            if (e) {
              this.academy = tmpAcademy;
              this.savedAcademy = cloneDeep(tmpAcademy);
            }
            this.loading = false;
          });
      })
      .catch((e) => {
        this.loading = false;
        if (e) this.showAlert('error', this.$t('message.error_generic'));
      });
    Api2()
      .get('admins/academy/' + this.$store.state.selectedAcademy + '/members')
      .then((response) => {
        const data = response.data;
        if (data) {
          for (const m of data.approved) {
            this.members.push({ value: m._id, text: m.fName + ' ' + m.lName });
          }
        }
      });
    Api2()
      .get(
        'admins/academy/' +
          this.$store.state.managedAcademy +
          '/is_connected/stripe',
      )
      .then((response) => {
        this.stripeReady = response.data.stripeConnected;
        this.stripe_state = response.data.stripeState;
        if (this.stripeReady) this.getTerminals();
      });
    this.getActivityActions();
    if (this.isSmallerDisplay()) this.altImageHeight = 'height:150px;';

    this.allPermissions = [
      { field: 'root', text: this.$t('message.permission_root') },
      {
        field: 'approve_member',
        text: this.$t('message.permission_approve_member'),
      },
      {
        field: 'view_attendance',
        text: this.$t('message.permission_view_attendance'),
      },
      {
        field: 'reject_member',
        text: this.$t('message.permission_reject_member'),
      },
      {
        field: 'create_waiver',
        text: this.$t('message.permission_create_waiver'),
      },
      { field: 'send_waiver', text: this.$t('message.permission_send_waiver') },
      { field: 'manage_documents', text: this.$t('message.permission_manage_documents') },
      {
        field: 'tablet_credentials',
        text: this.$t('message.permission_tablet_credentials'),
      },
      {
        field: 'view_updates',
        text: this.$t('message.permission_view_updates'),
      },
      {
        field: 'edit_schedule',
        text: this.$t('message.permission_edit_schedule'),
      },
      {
        field: 'edit_calendar',
        text: this.$t('message.permission_edit_calendar'),
      },
      {
        field: 'manage_event',
        text: this.$t('message.permission_manage_event'),
      },
      {
        field: 'view_products',
        text: this.$t('message.permission_view_products'),
      },
      {
        field: 'add_products',
        text: this.$t('message.permission_add_products'),
      },
      {
        field: 'edit_products',
        text: this.$t('message.permission_edit_products'),
      },
      { field: 'sell_items', text: this.$t('message.permission_sell_items') },
      {
        field: 'view_purchases',
        text: this.$t('message.permission_view_purchases'),
      },
      {
        field: 'view_payment_alerts',
        text: this.$t('message.permission_view_payment_alerts'),
      },
      {
        field: 'view_subscriptions',
        text: this.$t('message.permission_view_subscriptions'),
      },
      { field: 'view_income', text: this.$t('message.permission_view_income') },
      {
        field: 'issue_refund',
        text: this.$t('message.permission_issue_refund'),
      },
      {
        field: 'edit_subscription',
        text: this.$t('message.permission_edit_subscription'),
      },
      {
        field: 'manage_syllabus',
        text: this.$t('message.permission_manage_syllabus'),
      },
      {
        field: 'manage_syllabus_instances',
        text: this.$t('message.permission_syllabus_instance_data'),
      },
      {
        field: 'permission_social_wall',
        text: this.$t('message.permission_social_wall'),
      },
      {
        field: 'view_members_data',
        text: this.$t('message.view_members_data'),
      },
      { field: 'view_growth_data', text: this.$t('message.view_growth_data') },
      {
        field: 'get_system_mobile_notifications',
        text: this.$t('message.permission_get_system_mobile_notifications'),
      },
    ];
    this.filteredPermissions = this.allPermissions;
    Api2()
      .post('trial/stepState', {
        managedAcademy: this.$store.state.managedAcademy,
      })
      .then((response) => {
        if (response?.data?.academyProfile) {
          this.onboardingStepComplete = true;
        }
      })
      .catch(() => {
        this.onboardingStepComplete = true;
      })
      .then(() => {
        this.onboardingStepLoaded = true;
      });
  },
  computed: {
    publicPageUrl() {
      return this.academy && this.academy.identifier
        ? 'https://api.bjjlink.com/pub/' + this.academy.identifier
        : null;
    },
    academySettingsTourSteps() {
      return buildAcademySettingsTourSteps(this);
    },
    showAcademyProfileBanner() {
      return (
        !!this.$store.state.isOnboardingUser &&
        !!this.onboardingStepLoaded &&
        !this.onboardingStepComplete
      );
    },
    emailRules() {
      return [
        (v) =>
          !v ||
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
          this.$t('message.invalid_email'),
      ];
    },
    websiteRules() {
      return [
        (v) =>
          !v || /^https?:\/\/.+\..+/.test(v) || this.$t('message.invalid_url'),
      ];
    },
  },
  mounted() {
    this.tryStartAcademySettingsTour();
  },
  watch: {
    async '$route.fullPath'() {
      await this.$nextTick();
      this.tryStartAcademySettingsTour();
    },
    geolocatedAddress(val) {
      if (val && this.academy) this.academy.address = val;
    },
    selectedAdmin(val) {
      if (val) this.addAdmin(val);
    },
  },
  methods: {
    stripAcademySettingsTourQuery() {
      if (this.$route.query.tour !== 'true') {
        return;
      }
      const query = { ...this.$route.query };
      delete query.tour;
      this.$router
        .replace({
          path: this.$route.path,
          query,
        })
        .catch(() => {});
    },
    async tryStartAcademySettingsTour() {
      if (this._academySettingsTourLaunching) {
        return;
      }
      if (
        this.$route.name !== 'academysettings' ||
        this.$route.query.tour !== 'true'
      ) {
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));

      this._academySettingsTourLaunching = true;
      try {
        const appTour = this.$tours && this.$tours['custom-onboarding'];
        if (appTour && typeof appTour.stop === 'function') {
          appTour.stop();
        }
        this.$emit('expand-nav-for-tour');
        await this.$nextTick();
        const delayMs = 500;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        const onboarding = this.$refs.academySettingsTour;
        if (onboarding && typeof onboarding.start === 'function') {
          await onboarding.start();
        }
        this.stripAcademySettingsTourQuery();
      } finally {
        this._academySettingsTourLaunching = false;
      }
    },
    formatPhone(phone) {
      try {
        return phone
          .replace(/[^0-9]/g, '')
          .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      } catch (err) {
        return phone;
      }
    },
    onAffiliationChange(val) {
      if (!this.academy) return;
      this.academy.affiliation = val;
    },
    onCoverImageUploaded(uploadedFileData) {
      if (!uploadedFileData) return;
      const persistedUrl = uploadedFileData.original || uploadedFileData.location;
      this.altAcademyImage = persistedUrl;
      this.updateField('altImage', persistedUrl);
    },
    onLogoUploaded(uploadedFileData) {
      if (!uploadedFileData) return;
      this.academyImage = uploadedFileData.original;
      this.academy.image = uploadedFileData.location;
      this.updateField('image', this.academy.image);
      this.updateOnboardState();
    },
    updateField(field, value) {
      var newValue = value !== undefined ? value : this.academy[field];
        if (field == 'phone') {
          newValue = this.formatPhone(newValue);
          this.academy.phone = newValue;
        }
        try {
          const data = [
            {
              propName: field,
              value: newValue,
            },
          ];
          Api()
            .patch('admins/academy/' + this.$store.state.selectedAcademy, {
              values: data,
            })
            .then((response) => {
              const data = response.data;
              if (data) {
                if (field !== 'altImage' && field !== 'image') {
                  this.showAlert('success', this.$t('message.updated'));
                }
                this.updateOnboardState();
              }
            })
            .catch((e) => {
              if (e) {
                if (e.response.status == 403)
                  this.showAlert(
                    'error',
                    this.$t('message.academy_settings_admin_plus.oops_title'),
                    this.$t('message.academy_id_dup'),
                  );
                else
                  this.showAlert(
                    'error',
                    this.$t('message.academy_settings_admin_plus.oops_title'),
                    this.$t('message.error_generic'),
                  );
              }
            });
        } catch (e) {
          if (e) this.showAlert('error', this.$t('message.error_generic'));
        }
    },
    saveAcademyInfo() {
      if (this.academy.email && !this.isValidEmail(this.academy.email)) {
        this.showAlert('error', this.$t('message.invalid_email'));
        return;
      }
      try {
        const data = [
          { propName: 'name', value: this.academy.name },
          { propName: 'email', value: this.academy.email },
          { propName: 'description', value: this.academy.description },
          { propName: 'phone', value: this.academy.phone },
          { propName: 'website', value: this.academy.website },
          { propName: 'identifier', value: this.academy.identifier },
          { propName: 'affiliation', value: this.academy.affiliation },
        ];
        const academyInfoRequest = Api().patch(
          'admins/academy/' + this.$store.state.selectedAcademy,
          { values: data },
        );
        Promise.all([academyInfoRequest, this.updateColorTheme()])
          .then(([academyResponse]) => {
            if (academyResponse && academyResponse.data) {
              this.showAlert('success', this.$t('message.updated'));
              this.updateOnboardState();
            }
          })
          .catch((e) => {
            if (e) {
              if (e.response && e.response.status == 403)
                this.showAlert(
                  'error',
                  this.$t('message.academy_settings_admin_plus.oops_title'),
                  this.$t('message.academy_id_dup'),
                );
              else
                this.showAlert(
                  'error',
                  this.$t('message.academy_settings_admin_plus.oops_title'),
                  this.$t('message.error_generic'),
                );
            }
          });
      } catch (e) {
        if (e) this.showAlert('error', this.$t('message.error_generic'));
      }
    },
    clearAltImage() {
      this.academy.altImage = null;
      this.altAcademyImage = null;
      this.updateField('altImage', null);
    },
    updateColorTheme() {
      return Api().patch(
        'admins/academy/' + this.$store.state.managedAcademy + '/colors',
        { colorTheme: this.colorTheme },
      );
    },
    onColorPickerInput(val) {
      if (!this.colorTheme.currentEditing) return;
      let hex = null;
      if (typeof val === 'string') {
        hex = val;
      } else if (val && typeof val === 'object') {
        hex = val.hex || val.hexa || null;
      }
      if (hex) {
        if (hex.length === 9) hex = hex.slice(0, 7);
        this.$set(this.colorTheme, this.colorTheme.currentEditing, hex);
      }
    },
    showColorEditor(color) {
      this.colorTheme.currentEditing = color;
      this.colorEditorDialog = true;
    },
    cancelTabletCredentials() {
      this.academy.tabletCredentials = cloneDeep(
        this.savedAcademy.tabletCredentials,
      );
    },
    updateTabletCredentials() {
      var value = {
        password: this.academy.tabletCredentials.password,
        id: this.academy.tabletCredentials.id,
      };
      const data = [
        {
          propName: 'tabletCredentials',
          value: value,
        },
      ];
      Api()
        .patch('admins/academy/' + this.$store.state.selectedAcademy, {
          values: data,
        })
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert('success', this.$t('message.updated'));
          }
        })
        .catch((e) => {
          if (e) this.showAlert('error', this.$t('message.error_generic'));
        });
    },
    updateAcademyConfiguration() {
      const data = [
        {
          propName: 'configuration',
          value: this.academy.configuration,
        },
      ];
      Api()
        .patch('admins/academy/' + this.$store.state.selectedAcademy, {
          values: data,
        })
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert('success', this.$t('message.updated'));
          }
        })
        .catch((e) => {
          if (e) this.showAlert('error', this.$t('message.error_generic'));
        });
    },
    updateRequiredPrivateInfoFields() {
      const data = [
        {
          propName: 'requiredPrivateInfoFields',
          value: this.academy.requiredPrivateInfoFields,
        },
      ];
      Api()
        .patch('admins/academy/' + this.$store.state.selectedAcademy, {
          values: data,
        })
        .then(() => {
          this.showAlert('success', this.$t('message.updated'));
        })
        .catch((e) => {
          if (e) this.showAlert('error', this.$t('message.error_generic'));
        });
    },
    updateMissingInfoReminders() {
      this.updateAlternateEmails();
    },
    updateAlternateEmails() {
      Api()
        .patch(
          'admins/academy/' +
            this.$store.state.managedAcademy +
            '/automated-emails',
          { automatedEmails: this.academy.automatedEmails },
        )
        .then(() => {
          this.showAlert('success', this.$t('message.updated'));
        })
        .catch((e) => {
          if (e) this.showAlert('error', this.$t('message.error_generic'));
        });
    },
    getUpdates() {
      const filter = this.activityActionFilter;
      Api2()
        .get(
          'admins/academy/' +
            this.$store.state.managedAcademy +
            '/updates?' +
            (filter ? 'filter=' + filter : ''),
        )
        .then((response) => {
          this.updates = response.data;
        });
    },
    searchEmail() {
      this.getEmails();
    },
    getEmails() {
      this.loadingEmails = true;
      let options = {};
      if (this.selectedEmail) options.email = this.selectedEmail;
      if (this.failedEmailsFilter) options.failed = true;
      if (this.automationEmailsFilter) options.source = 'sequence-automations';

      Api()
        .post(
          'admins/academy/' + this.$store.state.managedAcademy + '/emails',
          options,
        )
        .then((response) => {
          this.emails = response.data.emails;
          this.loadingEmails = false;
        });
    },
    getAvailableEmails() {
      Api2()
        .get(
          'admins/academy/' +
            this.$store.state.managedAcademy +
            '/emails/available',
        )
        .then((response) => {
          this.availableEmails = response.data.availableEmails;
        });
    },
    getEmail(email) {
      email.opening = true;
      const id = email._id ? email._id : 'grouped';
      Api2()
        .get(
          'admins/academy/' +
            this.$store.state.managedAcademy +
            '/emails/' +
            id +
            '?' +
            (email.group ? 'group=' + email.group : ''),
        )
        .then((response) => {
          this.email = response.data.email;
          this.emailDialog = true;
          email.opening = false;
        });
    },
    getInitials(displayName) {
      if (!displayName) return '?';
      const parts = displayName.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    addAdmin(userId) {
      Api()
        .post(
          'admins/academy/' + this.$store.state.managedAcademy + '/admins',
          { adminId: userId },
        )
        .then((response) => {
          if (response.data) {
            this.managers = response.data;
            this.showAlert('success', this.$t('message.updated'));
          }
        })
        .catch((e) => {
          if (e) this.showAlert('error', this.$t('message.error_generic'));
        })
        .finally(() => {
          this.selectedAdmin = null;
        });
    },
    removeAdmin(id) {
      Api()
        .delete(
          'admins/academy/' +
            this.$store.state.managedAcademy +
            '/admins/' +
            id,
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.managers = data;
          }
        })
        .catch((e) => {
          if (e) this.showAlert('error', this.$t('message.error_generic'));
        });
    },
    viewAdminPermissions(id) {
      if (this.$store.state.managedAcademy) {
        this.loadingPermissions = true;
        this.selectedPermissions = null;
        Api2()
          .get(
            'admins/academy/' +
              this.$store.state.managedAcademy +
              '/admin/' +
              id +
              '/permissions',
          )
          .then((response) => {
            this.permissionsDialog = true;
            this.selectedPermissions = { adminId: id };
            if (
              response.data.permissions &&
              response.data.permissions.length > 0
            ) {
              if (response && response.data && response.data.permissions) {
                for (const p of response.data.permissions)
                  this.selectedPermissions[p] = true;
              }
            }
            for (const vp of this.validPermissions) {
              var existing = this.selectedPermissions[vp];
              if (!existing) this.selectedPermissions[vp] = false;
            }
            this.loadingPermissions = false;
          });
      } else {
        this.loadingPermissions = false;
        this.showAlert(
          'error',
          this.$t('message.academy_settings_admin_plus.pick_academy_again'),
        );
      }
    },
    updateAdminPermissions() {
      let permissionsArray = [];
      for (const p in this.selectedPermissions) {
        if (p != 'adminId' && this.selectedPermissions[p])
          permissionsArray.push(p);
      }
      if (this.$store.state.managedAcademy) {
        Api().patch(
          'admins/academy/' +
            this.$store.state.managedAcademy +
            '/admin/' +
            this.selectedPermissions.adminId +
            '/permissions',
          permissionsArray,
        );
      } else {
        this.navigateTo({ name: 'noacademy' });
      }
    },
    addUnsubscribedEmail() {
      Api()
        .patch(
          'admins/academy/' +
            this.$store.state.managedAcademy +
            '/emails/unsubscribed',
          {
            email: this.unsubscribedEmail,
          },
        )
        .then((response) => {
          if (!this.academy.unsubscribedEmails)
            this.academy.unsubscribedEmails = [];
          this.academy.unsubscribedEmails.push(response.data.email);
          this.unsubscribedEmail = null;
        })
        .catch((e) => {
          if (e) this.showAlert('error', this.$t('message.error_generic'));
        });
    },
    goToUnsubscribedEmailUser(e) {
      if (e.user) this.goToMember(e.user);
    },
    addAlternativeEmail(prop) {
      const email = this.newAlternativeEmails[prop]
        ? this.newAlternativeEmails[prop].trim().toLowerCase()
        : null;
      const validEmail = this.isValidEmail(email);
      if (!validEmail) {
        this.showAlert('error', this.$t('message.invalid_email'));
        return;
      }
      const existingEmails = this.academy.automatedEmails[prop].emails;
      if (existingEmails.indexOf(email) >= 0) {
        this.showAlert('error', this.$t('message.duplicate'));
        return;
      }
      this.academy.automatedEmails[prop].emails.push(email);
      this.newAlternativeEmails[prop] = null;
    },
    removeAlternativeEmail(prop, val) {
      const index = this.academy.automatedEmails[prop].emails.indexOf(val);
      if (index >= 0) {
        this.academy.automatedEmails[prop].emails.splice(val);
      } else {
        this.showAlert('error', this.$t('message.error_generic'));
      }
    },
    removeAutomatedEmail(type, email) {
      const list = this.academy.automatedEmails[type];
      if (list && list.emails) {
        const index = list.emails.indexOf(email);
        if (index >= 0) list.emails.splice(index, 1);
      }
    },
    getEmailTemplates() {
      Api2()
        .get(
          'documents/academy/' +
            this.$store.state.managedAcademy +
            '/email/templates?archived=false',
        )
        .then((response) => {
          if (response) {
            if (response.data.templates && response.data.templates.length > 0)
              this.emailTemplates = response.data.templates;
          }
        });
    },
    addNewExraField() {
      this.contactFormExtraFields.push({ fieldName: null, required: false });
    },
    updateContactFormExtraFields() {
      Api()
        .patch(
          'admins/academy/' +
            this.$store.state.managedAcademy +
            '/contact-form',
          { extraFields: this.contactFormExtraFields },
        )
        .then(() => {
          this.showAlert('success', this.$t('message.updated'));
        })
        .catch(() => {
          this.showAlert('error', this.$t('message.error_generic'));
        });
    },
    removeExtraField(index) {
      this.contactFormExtraFields.splice(index, 1);
    },
    getActivityActions() {
      if (!this.updates) {
        Api2()
          .get(
            'admins/academy/' +
              this.$store.state.managedAcademy +
              '/activities/distinct/actions',
          )
          .then((response) => {
            const tmp = [];
            for (const a of response.data) {
              tmp.push({ text: this.$t('message.' + a), value: a });
            }
            this.activityActions = tmp;
          });
      } else {
        this.updates = null;
      }
    },
    updateOnboardState() {
      if (
        !this.onboardingStepComplete &&
        this.academy.name &&
        this.academy.address &&
        this.academy.description &&
        this.academy.email &&
        this.academyImage
      ) {
        completeOnboardingStep({
          step: 'academyProfile',
          managedAcademy: this.$store.state.managedAcademy,
          store: this.$store,
        }).then(({ stepState }) => {
          this.onboardingStepComplete = !!stepState?.academyProfile;
        });
      }
    },
  },
};
</script>

<style scoped>
.v-expansion-panel {
  border: 1px solid var(--v-plusBorder-base);
}

.v-expansion-panel::after {
  border: 0;
}

.v-expansion-panel::before {
  box-shadow: none !important;
}

::v-deep .missingAcademyProfileHighlight .v-input__slot {
  background: #FFF8E1;
  border-color: #FFCC80;
}

::v-deep .missingAcademyProfileHighlight .v-input__slot fieldset {
  border-color: #FFCC80;
}

::v-deep .theme--dark .missingAcademyProfileHighlight .v-input__slot {
  background: rgba(255, 243, 224, 0.10);
}



.missingAcademyLogoHighlight {
  background: #FFF8E1;
  border: 1px solid #FFCC80;
  padding: 10px;
}


.theme--dark .missingAcademyLogoHighlight {
  background: transparent;
  border: 1px solid #FFCC80;
  padding: 10px;
}

.theme--dark .missingAcademyLogoHighlight > div {
  background: rgba(255, 243, 224, 0.10);
}

</style>
